import { useMemo, useState } from "react";
import { SimpleTable } from "@/core/components/simple-table";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import {
  FIELD_COLUMN_MIN_WIDTH,
  TeamCol,
  TEAM_COLUMN_WIDTH,
  getRosterColumnWidths,
  ROSTER_NAME_COLUMN_ID,
  ReadOnlyNameCol,
  ReadOnlyRosterFieldCol,
  type ReadOnlyRosterRow,
} from "./cols";
import type { PayrollRosterField, PayrollRosterMember } from "./types";
import { sortedRosterFields } from "./utils";

interface RosterReadOnlyTableProps {
  fields: PayrollRosterField[];
  members: PayrollRosterMember[];
}

// The roster exactly as a run saw it — same columns and money/percent
// formatting as the editor's grid, but with none of the edit affordances (no
// row button, no badges, no change-bar). Pure view.
export const RosterReadOnlyTable = ({
  fields,
  members,
}: RosterReadOnlyTableProps) => {
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 25 });
  const orderedFields = useMemo(() => sortedRosterFields(fields), [fields]);

  const rows = useMemo<ReadOnlyRosterRow[]>(
    () =>
      members.map((member, index) => ({
        id: `${member.service_titan_employee_id || "row"}-${index}`,
        member,
      })),
    [members],
  );

  const { columns, tableMinWidth } = useMemo(() => {
    const widths = getRosterColumnWidths(members, orderedFields);

    const showTeams = members.some(
      (member) => (member.team_access_keys?.length ?? 0) > 0,
    );
    return {
      columns: [
        ReadOnlyNameCol(widths.name),
        ...(showTeams ? [TeamCol<ReadOnlyRosterRow>()] : []),
        ...orderedFields.map((field) =>
          ReadOnlyRosterFieldCol(
            field,
            widths.fields.get(field.key) ?? FIELD_COLUMN_MIN_WIDTH,
          ),
        ),
      ],
      tableMinWidth: widths.tableMinWidth + (showTeams ? TEAM_COLUMN_WIDTH : 0),
    };
  }, [members, orderedFields]);

  if (rows.length === 0) {
    return (
      <Stack
        align="center"
        justify="center"
        className="min-h-0 w-full flex-1 px-6 text-center"
      >
        <Typography variant="subtitleW1">No people on this roster</Typography>
      </Stack>
    );
  }

  return (
    // SimpleTable needs a full-width, definite-height container; flex-1 + min-h-0
    // hands it the rest of the card so it scrolls internally, and
    // scrollContentStyle sets the dynamic min-width so wide rosters scroll
    // sideways with the Name column pinned as the identity anchor.
    <Stack gap="none" fullWidth className="min-h-0 flex-1">
      <SimpleTable
        data={rows}
        columns={columns}
        getRowId={(row) => row.id}
        enablePagination
        paginationState={pagination}
        onPaginationStateChange={setPagination}
        scrollResetKey={`${pagination.pageIndex}-${pagination.pageSize}`}
        className="h-full w-full"
        scrollContentStyle={{ minWidth: `${tableMinWidth}px` }}
        columnPinning={{ left: [ROSTER_NAME_COLUMN_ID] }}
        respectColumnWidths
      />
    </Stack>
  );
};
